import { useState, useEffect } from "react";
import useAccountEnhanced from "../../../hooks/use-account-enhanced";
import { Card } from "src/components/card";
import { RepresentativeRaw } from "../types/RepresentativeRaw";
import { Representative } from "../types/Representative";
import { HRAssignee } from "../types/HRAssignee";
import { OrgPayload } from "../types/OrgPayload";
import { useQuery } from "@tanstack/react-query";
import { hrisConfigMutations, hrisConfigQueryOptions } from "src/api/mvte/hris-rep-query-options";
import dayjs from "dayjs";

function mapToRepresentative(raw: RepresentativeRaw): Representative
{
    return {
        eorName: raw.eorName,
        ccName: raw.ccName,
        hrPOCStaff: raw.hrPOCStaff,
        hrPOCDriver: raw.hrPOCDriver,
        isActive: raw.Active,
        ModifiedDate: raw.ModifiedDate,
        EmployerOfRecordID: raw.EmployerOfRecordID,
        ClientCompanyID: raw.ClientCompanyID,
        AssignedHRPOCStaff: raw.AssignedHRPOCStaff,
        AssignedHRPOCDriver: raw.AssignedHRPOCDriver,
    };
}

function ModifyRepresentatives()
{
    const accountEnhanced = useAccountEnhanced();
    const [searchTerm, setSearchTerm] = useState("");
    const [columnFilters, setColumnFilters] = useState(
    {
        eorName: "",
        ccName: "",
        hrPOCStaff: "",
        hrPOCDriver: "",
        ModifiedDate: ""
    });
    const [sortDir, setSortDir] = useState<true | false>(true)
    const [data, setData] = useState<Representative[]>([]);
    const [editingId, setEditingId] = useState<{eorID: number; ccID: number;} | null>(null);

    const [editValues, setEditValues] = useState<
    {
        hrPOCStaff: string;
        hrPOCStaffID: number;
        hrPOCDriver: string;
        hrPOCDriverID: number;
    }> ({ hrPOCStaff: "", hrPOCStaffID: 0, hrPOCDriver: "", hrPOCDriverID: 0});

    const hrOrgRepQuery = useQuery({ ...hrisConfigQueryOptions.hrOrgRepQueryOptions() });
    const hrAssigneeQuery = useQuery({ ...hrisConfigQueryOptions.hrAssigneeQueryOptions() });
    const hrOrgMutation = hrisConfigMutations.useUpdateHRAssignments();

    useEffect(() =>
    {
        if (hrOrgRepQuery.data)
        {
            setData(hrOrgRepQuery.data.map(mapToRepresentative));
        }
    }, [hrOrgRepQuery.data]);

    const assigneeOptions = (hrAssigneeQuery.data  ?? []).map((a: HRAssignee) =>
    ({
        EmployeeUID: a.EmployeeUID,
        EmployeeName: a.EmployeeName,
    }));

    const filteredData = data.filter((representatives) =>
    {
        const employerOfRecord = `${representatives.eorName}`.toLowerCase();
        const clientCompany = representatives.ccName.toLowerCase();
        const staffPOC = (representatives.hrPOCStaff ?? "").toLowerCase();
        const driverPOC = (representatives.hrPOCDriver ?? "").toLowerCase();
        const modifiedDate = dayjs(representatives.ModifiedDate).format("MM/D/YYYY h:mm A").toLowerCase();

        // Main search: matches if the term appears in any of the four fields
        if (searchTerm.trim() !== "")
        {
            const term = searchTerm.toLowerCase();
            const matchesMain =
                employerOfRecord.includes(term) ||
                clientCompany.includes(term) ||
                staffPOC.includes(term) ||
                driverPOC.includes(term) ||
                modifiedDate.includes(term);
            if (!matchesMain) return false;
        }

        // Per-column filters: each one must match its own column
        if (columnFilters.eorName.trim() !== "" && !employerOfRecord.includes(columnFilters.eorName.toLowerCase())) return false;
        if (columnFilters.ccName.trim() !== "" && !clientCompany.includes(columnFilters.ccName.toLowerCase())) return false;
        if (columnFilters.hrPOCStaff.trim() !== "" && !staffPOC.includes(columnFilters.hrPOCStaff.toLowerCase())) return false;
        if (columnFilters.hrPOCDriver.trim() !== "" && !driverPOC.includes(columnFilters.hrPOCDriver.toLowerCase())) return false;
        if (columnFilters.ModifiedDate.trim() !== "" && !modifiedDate.includes(columnFilters.ModifiedDate.toLowerCase())) return false;
        return true;
    });

    const sortData = sortDir === null ? filteredData : [...filteredData].sort((a,b) =>
    {
            const diff = dayjs(a.ModifiedDate).valueOf() - dayjs(b.ModifiedDate).valueOf();
            return sortDir ? diff : -diff;
    })

    function handleEdit(rep: Representative)
    {
        console.log(assigneeOptions)
        setEditingId({eorID: rep.EmployerOfRecordID, ccID: rep.ClientCompanyID});
        setEditValues
        ({
            hrPOCStaff: rep.hrPOCStaff,
            hrPOCStaffID: rep.AssignedHRPOCStaff,
            hrPOCDriver: rep.hrPOCDriver,
            hrPOCDriverID: rep.AssignedHRPOCDriver,
        });
    }

    async function handleConfirm()
    {
        if (editingId === null) return;
        const rep = data.find((r) => 
            r.EmployerOfRecordID === editingId.eorID &&
            r.ClientCompanyID === editingId.ccID
        );
        if (!rep) return;

        const noChanges =
            editValues.hrPOCStaffID === rep.AssignedHRPOCStaff &&
            editValues.hrPOCDriverID === rep.AssignedHRPOCDriver;

        if (noChanges)
        {
            setEditingId(null);
            return;
        }

        const newPayload: OrgPayload =
        {
            eorID: rep.EmployerOfRecordID,
            ccID: rep.ClientCompanyID,
            pocStaff: Number(editValues.hrPOCStaffID),
            pocDriver: Number(editValues.hrPOCDriverID),
            SourceSystem: "MVTE-Center",
            ChangedBy: accountEnhanced.me.employeeCode,
            Note: "Hardcoded note for now",
        };

        await hrOrgMutation.mutateAsync({payload: newPayload});

        setData((prev) => prev.map((r) => 
            r.EmployerOfRecordID === editingId.eorID && 
            r.ClientCompanyID === editingId.ccID ?
        {
            ...r,
            hrPOCStaff: editValues.hrPOCStaff,
            hrPOCDriver: editValues.hrPOCDriver,
            AssignedHRPOCStaff: editValues.hrPOCStaffID,
            AssignedHRPOCDriver: editValues.hrPOCDriverID,
        } : r ));
        setEditingId(null);
    }

    function handleCancel()
    {
        setEditingId(null);
    }

    if (hrOrgRepQuery.isLoading)
    {
        return (
            <Card>
                <div style={{ padding: "24px", textAlign: "center", color: "#555", fontSize: "15px" }}>
                    Loading representatives...
                </div>
            </Card>
        );
    }

    return (
        <Card>
            <div style={{ padding: "24px" }}>
                <div style={toolbarStyle}>
                    <input type="text" placeholder="Search by value..." value={searchTerm} onChange= {(e) => setSearchTerm(e.target.value)} style={searchInputStyle}/>
                </div>
                <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "auto", border: "1px solid #000;"}}>
                    <thead>
                        <tr style={{ backgroundColor: "#f0f0f0" }}>
                            <th style={thStyle}>Employer of Record</th>
                            <th style={thStyle}>Client Company</th>
                            <th style={thStyle}>Staff POC</th>
                            <th style={thStyle}>Driver POC</th>
                            <th style={thStyle}>
                            Modified Date {"    "}
                            <button onClick={() => setSortDir(!sortDir)} title="Toggle ASC|DESC by Modified Date" style={sortButtonStyle}>
                                {sortDir ? "▲" : "▼"}
                            </button>
                        </th>
                            <th style={thStyle}>Active</th>
                            <th style={{ ...thStyle, width: "160px" }}>Actions</th>
                        </tr>
                        <tr style={{ backgroundColor: "#f7f7f7" }}>
                            <th style={thStyle}>
                                <input
                                    type="text"
                                    placeholder="Filter..."
                                    value={columnFilters.eorName}
                                    onChange={(e) => setColumnFilters((prev) => ({ ...prev, eorName: e.target.value }))}
                                    style={columnSearchInputStyle}
                                />
                            </th>
                            <th style={thStyle}>
                                <input
                                    type="text"
                                    placeholder="Filter..."
                                    value={columnFilters.ccName}
                                    onChange={(e) => setColumnFilters((prev) => ({ ...prev, ccName: e.target.value }))}
                                    style={columnSearchInputStyle}
                                />
                            </th>
                            <th style={thStyle}>
                                <input
                                    type="text"
                                    placeholder="Filter..."
                                    value={columnFilters.hrPOCStaff}
                                    onChange={(e) => setColumnFilters((prev) => ({ ...prev, hrPOCStaff: e.target.value }))}
                                    style={columnSearchInputStyle}
                                />
                            </th>
                            <th style={thStyle}>
                                <input
                                    type="text"
                                    placeholder="Filter..."
                                    value={columnFilters.hrPOCDriver}
                                    onChange={(e) => setColumnFilters((prev) => ({ ...prev, hrPOCDriver: e.target.value }))}
                                    style={columnSearchInputStyle}
                                />
                            </th>
                            <th style={thStyle}>
                                <input
                                    type="text"
                                    placeholder="Filter..."
                                    value={columnFilters.ModifiedDate}
                                    onChange={(e) => setColumnFilters((prev) => ({ ...prev, ModifiedDate: e.target.value }))}
                                    style={columnSearchInputStyle}
                                />
                            </th>
                            <th style={thStyle}></th>
                            <th style={{ ...thStyle, width: "160px" }}></th>
                        </tr>
                    </thead>

                    <tbody>
                        {
                            sortData.map((rep) => { const isEditing = editingId !== null && editingId?.eorID === rep.EmployerOfRecordID && editingId.ccID === rep.ClientCompanyID;
                            return (
                                <tr key={`${rep.EmployerOfRecordID}-${rep.ClientCompanyID}`}>
                                    <td style={tdStyle}>{rep.eorName}</td>
                                    <td style={tdStyle}>{rep.ccName}</td>
                                    <td style={tdStyle}>
                                    {
                                        isEditing ?
                                        (
                                            <select value={editValues.hrPOCStaffID} onChange={(e) =>
                                            {
                                                const selected = assigneeOptions.find(o => o.EmployeeUID === Number(e.target.value))!;
                                                setEditValues((prev) => ({ ...prev, hrPOCStaff: selected.EmployeeName, hrPOCStaffID: selected.EmployeeUID }));
                                            }}
                                            style={selectStyle}>
                                            {
                                                assigneeOptions.map((opt) =>
                                                (
                                                    <option key={opt.EmployeeUID} value={opt.EmployeeUID}>{opt.EmployeeName}</option>
                                                ))
                                            }
                                            </select>
                                        ) : (rep.hrPOCStaff)
                                    }
                                    </td>

                                    <td style={tdStyle}>
                                    {
                                        isEditing ?
                                        (
                                            <select value={editValues.hrPOCDriverID} onChange={(e) =>
                                            {
                                                const selected = assigneeOptions.find(o => o.EmployeeUID === Number(e.target.value))!;
                                                setEditValues((prev) => ({ ...prev, hrPOCDriver: selected.EmployeeName, hrPOCDriverID: selected.EmployeeUID }));
                                            }}
                                            style={selectStyle}>
                                            {
                                                assigneeOptions.map((opt) =>
                                                (
                                                    <option key={opt.EmployeeUID} value={opt.EmployeeUID}>{opt.EmployeeName}</option>
                                                ))
                                            }
                                            </select>
                                        ) : (rep.hrPOCDriver)
                                    }

                                    </td>

                                    <td style={tdStyle}>
                                        <span title={rep.ModifiedDate} style={{ cursor: "default" }}>
                                        {
                                            dayjs(rep.ModifiedDate).format("MM/D/YYYY h:mm A")
                                        }
                                        </span>
                                    </td>
                                    
                                    <td style={tdStyle}>{rep.isActive ? "Yes" : "No"}</td>                                    

                                    <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                                    {
                                        isEditing ?
                                        (
                                            <>
                                                <button onClick={handleConfirm} style={confirmButtonStyle}>Confirm</button>
                                                <button onClick={handleCancel} style={cancelButtonStyle}>Cancel</button>
                                            </>
                                        ) : ( <button onClick={() => handleEdit(rep)} style={editButtonStyle}>Edit</button> )
                                    }
                                    </td>
                                </tr>
                            );
                            })
                        }
                    </tbody>
                </table>
            </div>
        </Card>
    );
}

const thStyle: React.CSSProperties =
{
    border: "1px solid #ccc",
    padding: "10px 12px",
    textAlign: "center",
    fontWeight: 600,
};
const tdStyle: React.CSSProperties =
{
    border: "1px solid #ccc",
    padding: "10px 12px",
    textAlign: "center",
    fontSize: "14px",
};
const selectStyle: React.CSSProperties =
{
    width: "100%",
    padding: "4px 6px",
    fontSize: "14px",
    border: "1px solid #aaa",
    borderRadius: "4px",
};
const columnSearchInputStyle: React.CSSProperties =
{
    width: "100%",
    padding: "4px 6px",
    fontSize: "12px",
    fontWeight: 400,
    border: "1px solid #ccc",
    borderRadius: "4px",
    outline: "none",
    boxSizing: "border-box",
    color: "#222",
};
const editButtonStyle: React.CSSProperties =
{
    padding: "4px 12px",
    fontSize: "13px",
    cursor: "pointer",
    backgroundColor: "#0078d4",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
};
const confirmButtonStyle: React.CSSProperties =
{
    padding: "4px 10px",
    fontSize: "13px",
    cursor: "pointer",
    backgroundColor: "#2e7d32",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    marginRight: "6px",
};
const cancelButtonStyle: React.CSSProperties =
{
    padding: "4px 10px",
    fontSize: "13px",
    cursor: "pointer",
    backgroundColor: "#999",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
};
const toolbarStyle: React.CSSProperties =
{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "10px",
    marginBottom: "12px",
};
const searchInputStyle: React.CSSProperties =
{
    flex: 1,
    maxWidth: "320px",
    padding: "6px 10px",
    fontSize: "13px",
    border: "1px solid #ccc",
    borderRadius: "4px",
    outline: "none",
    color: "#222",
}
const sortButtonStyle: React.CSSProperties = 
{
    padding: "0.6px",
    fontSize: "12px",
    cursor: "pointer",
    backgroundColor: "transparent",
    border: "1px solid #aaa",
    borderRadius: "4px",
    lineHeight: "1.6",
}
export { ModifyRepresentatives };